import React, { useEffect, useRef, useState } from "react";
import "./Loader.scss";
import ClassNames from "classnames";
import { TextButton } from "../../controls";

interface LoaderActionTooltip {
  content: string;
}

interface LoaderAction {
  label?: string;
  // Forwarded to TextButton, which invokes it with the click event.
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  tooltip?: string;
  withTooltip?: LoaderActionTooltip;
  leftIcon?: string;
}

export interface LoaderProps {
  className?: string;
  fullViewportHeight?: boolean;
  fullHeight?: boolean;
  wrapperHeight?: boolean;
  center?: boolean;
  flexcenter?: boolean;
  placeholder?: boolean;
  xlTitle?: boolean;
  largeTitle?: boolean;
  // Rendered directly as JSX children, so any ReactNode is valid.
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  text?: React.ReactNode;
  icon?: string;
  smallIcon?: boolean;
  hasBorder?: boolean;
  recolor?: boolean;
  noBackground?: boolean;
  noPaddingBottom?: boolean;
  noIconMargin?: boolean;
  largeButtons?: boolean;
  action?: LoaderAction | null;
  secondaryAction?: LoaderAction | null;
  tertiaryAction?: LoaderAction | null;
  img?: string;
  iconSize?: string | number;
  alignTop?: boolean;
  verticalButtons?: boolean;
  "data-testid"?: string;
}

const Loader: React.FC<LoaderProps> = (props) => {
  const {
    flexcenter,
    center,
    text,
    title,
    subtitle,
    icon,
    smallIcon,
    action,
    secondaryAction,
    tertiaryAction,
    img,
    iconSize,
    placeholder,
    noBackground,
    alignTop,
    fullViewportHeight,
    largeTitle,
    xlTitle,
    largeButtons,
    noIconMargin,
    fullHeight,
    hasBorder,
    recolor,
    wrapperHeight,
    noPaddingBottom,
    verticalButtons,
    "data-testid": dataTestId,
  } = props;

  const [containerHeight, setContainerHeight] = useState(0);
  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const parentDiv = loaderRef.current?.parentNode as HTMLElement | null;

    if (parentDiv) {
      const updateHeight = () => {
        const height = parentDiv.clientHeight;
        setContainerHeight(height);
      };

      // Initialize the height
      updateHeight();

      // Use ResizeObserver to dynamically update the height
      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(parentDiv);

      return () => {
        // Clean up the ResizeObserver on unmount
        resizeObserver.disconnect();
      };
    }
  }, []);

  const classNameWrapper = ClassNames(
    "loader-wrap",
    {
      "has-text": text,
      "has-border": hasBorder,
      "has-subtitle": subtitle,
      "has-title": title,
      "has-action": action,
      "has-recolor": recolor,
      "has-secondary-action": secondaryAction,
      "has-tertiary-action": tertiaryAction,
      "is-placeholder": placeholder,
      "is-center": center,
      "is-flexcenter": flexcenter,
      "no-background": noBackground,
      "has-align-top": alignTop,
      "is-viewport-height": fullViewportHeight,
      "has-large-title": largeTitle,
      "has-xl-title": xlTitle,
      "has-large-buttons": largeButtons,
      "no-icon-margin": noIconMargin,
      "is-small-icon": smallIcon,
      "is-fullheight": fullHeight,
      "is-wrapperHeight": wrapperHeight,
      "has-no-padding-bottom": noPaddingBottom,
      [`size-${iconSize}`]: iconSize,
      "has-only-icon":
        !text && !subtitle && !title && !action && !secondaryAction && !tertiaryAction,
      "has-vertical-buttons": verticalButtons,
    },
    props.className
  );

  return (
    <div
      ref={loaderRef}
      style={{ height: wrapperHeight ? containerHeight : undefined }}
      className={classNameWrapper}
      data-testid={
        dataTestId ? dataTestId : title || text ? "no-data" : "loader"
      }
    >
      {alignTop ? <div className="loader-alignment-div-top" /> : ""}
      <div className="loader">
        {img ? (
          <div className="loader-icon">
            <img src={img} alt="Loader" />
          </div>
        ) : (
          ""
        )}
        {icon ? (
          <div className="loader-icon-wrap">
            <i className={`seem-icon ${icon}`} />
          </div>
        ) : (
          ""
        )}
        {text ? <div className="loader-text">{text ? text : ""}</div> : ""}
        {title ? <div className="title-text">{title ? title : ""}</div> : ""}
        {subtitle ? (
          <div className="subtitle-text">{subtitle ? subtitle : ""}</div>
        ) : (
          ""
        )}
        {!!secondaryAction || !!action || !!tertiaryAction ? (
          <div
            className={`form-button-group ${verticalButtons ? "is-vertical" : ""}`}
          >
            {!!tertiaryAction && (
              <TextButton
                disabled={tertiaryAction.disabled || false}
                data-testid={"loader-tertiary-action"}
                wrapperClassName={"loader-action"}
                label={tertiaryAction.label}
                size={largeButtons ? "large" : ""}
                onClick={tertiaryAction.onClick}
                withTooltip={tertiaryAction.withTooltip || undefined}
                leftIcon={tertiaryAction.leftIcon || undefined}
              />
            )}
            {!!secondaryAction && (
              <TextButton
                disabled={secondaryAction.disabled || false}
                data-testid={"loader-action"}
                wrapperClassName={"loader-action"}
                label={secondaryAction.label}
                size={largeButtons ? "large" : ""}
                onClick={secondaryAction.onClick}
                withTooltip={secondaryAction.withTooltip || undefined}
                leftIcon={secondaryAction.leftIcon || undefined}
              />
            )}
            {!!action && (
              <TextButton
                data-testid={"loader-action"}
                wrapperClassName={"loader-action"}
                buttonStyle={"primary"}
                disabled={action?.disabled}
                withTooltip={
                  action?.tooltip ? { content: action?.tooltip } : undefined
                }
                label={action.label}
                size={largeButtons ? "large" : ""}
                onClick={action.onClick}
              />
            )}
          </div>
        ) : (
          ""
        )}
      </div>
    </div>
  );
};

export default Loader;
